import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  InputAdornment,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Collapse,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  Scale as ScaleIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon,
  Inventory2 as Inventory2Icon,
  FilterAlt as FilterAltIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  AccessTime as AccessTimeIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Category as CategoryIcon,
  Sell as SellIcon,
  Grade as GradeIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import MarkExcessModal from '../modals/MarkExcessModal';
import ReturnToInventoryModal from '../modals/ReturnToInventoryModal';
import ImportDataModal from '../modals/ImportDataModal';
import ExportButton from '../../utils/ExportButton';
import { PRODUCTS, GRADES, BRANDS, PACK_SIZES } from '../../utils/DROPDOWN_OPTIONS';

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const API_BASE_URL = 'https://ccc4cs.choicegroup.in/api';

// Calculate total pounds from pack_size, cartons, and loose_bags
const calculateTotalPounds = (packSize, cartons, looseBags = 0) => {
  if (!packSize || (!cartons && !looseBags)) return 0;
  
  // Replace X with / for consistency
  const normalizedPackSize = packSize.replace(/X/gi, '/');
  
  // Parse pack_size format: "X/Y UNIT" (e.g., "3/7 LB", "12/16 OZ", "1/10 KG")
  const match = normalizedPackSize.match(/^(\d+)\/(\d+)\s*(LB|OZ|KG)$/i);
  
  if (!match) {
    if (normalizedPackSize.toUpperCase() === 'LOOSE KG') {
      return 0;
    }
    return 0;
  }
  
  const bagsPerCarton = parseInt(match[1], 10);
  const weightPerBag = parseInt(match[2], 10);
  const unit = match[3].toUpperCase();
  
  let totalPounds = 0;
  
  if (unit === 'LB') {
    totalPounds = (bagsPerCarton * weightPerBag * cartons) + (looseBags * weightPerBag);
  } else if (unit === 'OZ') {
    totalPounds = ((bagsPerCarton * weightPerBag * cartons) / 16) + ((looseBags * weightPerBag) / 16);
  } else if (unit === 'KG') {
    totalPounds = (bagsPerCarton * weightPerBag * cartons * 2.2) + (looseBags * weightPerBag * 2.2);
  }
  
  return Math.round(totalPounds * 100) / 100;
};

const ShipmentsPage = ({ user }) => {
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };

  const [shipments, setShipments] = useState([]);
  const [summary, setSummary] = useState({
    total_shipments: 0,
    total_pallets: 0,
    total_items: 0,
    total_cartons: 0,
    total_pounds: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [fclMonth, setFclMonth] = useState('');
  const [fclNo, setFclNo] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [fclMonths, setFclMonths] = useState([]);
  const [fclNumbers, setFclNumbers] = useState([]);
  const [poNumbers, setPoNumbers] = useState([]);
  
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedFcl, setExpandedFcl] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedProductInShipment, setSelectedProductInShipment] = useState({});
  
  const [showMarkExcessModal, setShowMarkExcessModal] = useState(false);
  const [showReturnToInventoryModal, setShowReturnToInventoryModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);

  // Analytics drill-down modal state
  const [analyticsModal, setAnalyticsModal] = useState({
    open: false,
    type: '', // 'product', 'brand', 'grade'
    name: '',
    data: []
  });

  useEffect(() => {
    fetchShipments();
    fetchFilterOptions();
  }, [fclMonth, fclNo, poNumber, startDate, endDate, productFilter, gradeFilter, brandFilter, searchTerm]);

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        action: 'list',
        ...(fclMonth && { fcl_month: fclMonth }),
        ...(fclNo && { fcl_no: fclNo }),
        ...(poNumber && { po_number: poNumber }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
        ...(productFilter && { product: productFilter }),
        ...(gradeFilter && { grade: gradeFilter }),
        ...(brandFilter && { brand: brandFilter }),
        ...(searchTerm && { search: searchTerm })
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/shipments.php?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      const data = await response.json();

      if (data.success) {
        setShipments(data.data);
        setSummary(data.summary);
      } else {
        setError(data.error || 'Failed to fetch shipments');
      }
    } catch (err) {
      console.error('Error fetching shipments:', err);
      setError('Failed to fetch shipments');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [monthsRes, numbersRes, poRes] = await Promise.all([
        fetch(`${API_BASE_URL}/shipments.php?action=fcl_months`, { credentials: 'include', headers }),
        fetch(`${API_BASE_URL}/shipments.php?action=fcl_numbers`, { credentials: 'include', headers }),
        fetch(`${API_BASE_URL}/shipments.php?action=po_numbers`, { credentials: 'include', headers })
      ]);

      const [monthsData, numbersData, poData] = await Promise.all([
        monthsRes.json(),
        numbersRes.json(),
        poRes.json()
      ]);

      if (monthsData.success) setFclMonths(monthsData.data);
      if (numbersData.success) setFclNumbers(numbersData.data);
      if (poData.success) setPoNumbers(poData.data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  // Get all items for analytics
  const allItems = useMemo(() => {
    const items = [];
    shipments.forEach(shipment => {
      if (shipment.items) {
        shipment.items.forEach(item => {
          items.push({ ...item, fcl_month: shipment.fcl_month, fcl_no: shipment.fcl_no, po_number: shipment.po_number });
        });
      }
    });
    return items;
  }, [shipments]);

  const shipmentsGroupedByMonth = useMemo(() => {
    const grouped = {};
    shipments.forEach(shipment => {
      const month = shipment.fcl_month || 'NO MONTH';
      if (!grouped[month]) {
        grouped[month] = {
          month,
          shipments: [],
          totals: { cartons: 0, pounds: 0, pallets: 0 }
        };
      }
      grouped[month].shipments.push(shipment);
      grouped[month].totals.cartons += shipment.totals?.cartons || 0;
      // Calculate pounds from individual items using the correct formula
      const shipmentPounds = (shipment.items || []).reduce((sum, item) => 
        sum + calculateTotalPounds(item.pack_size, item.cartons, item.loose_bags), 0);
      grouped[month].totals.pounds += shipmentPounds;
      grouped[month].totals.pallets += shipment.pallet_count || 0;
    });
    return Object.values(grouped).sort((a, b) => {
      const monthOrder = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      const [aMonth, aYear] = (a.month || '').split('-');
      const [bMonth, bYear] = (b.month || '').split('-');
      if (aYear !== bYear) return parseInt(bYear) - parseInt(aYear);
      return monthOrder.indexOf(bMonth) - monthOrder.indexOf(aMonth);
    });
  }, [shipments]);

  const analytics = useMemo(() => {
    const productStats = {};
    const brandStats = {};
    const gradeStats = {};
    
    allItems.forEach(item => {
      const prodKey = item.product || 'Unknown';
      if (!productStats[prodKey]) {
        productStats[prodKey] = { name: prodKey, cartons: 0, pounds: 0, count: 0, items: [] };
      }
      productStats[prodKey].cartons += parseInt(item.cartons) || 0;
      productStats[prodKey].pounds += calculateTotalPounds(item.pack_size, item.cartons, item.loose_bags);
      productStats[prodKey].count++;
      productStats[prodKey].items.push(item);
      
      const brandKey = item.brand || 'Unknown';
      if (!brandStats[brandKey]) {
        brandStats[brandKey] = { name: brandKey, cartons: 0, count: 0, items: [] };
      }
      brandStats[brandKey].cartons += parseInt(item.cartons) || 0;
      brandStats[brandKey].count++;
      brandStats[brandKey].items.push(item);
      
      const gradeKey = item.grade || 'Unknown';
      if (!gradeStats[gradeKey]) {
        gradeStats[gradeKey] = { name: gradeKey, cartons: 0, count: 0, items: [] };
      }
      gradeStats[gradeKey].cartons += parseInt(item.cartons) || 0;
      gradeStats[gradeKey].count++;
      gradeStats[gradeKey].items.push(item);
    });
    
    const topProducts = Object.values(productStats).sort((a, b) => b.cartons - a.cartons).slice(0, 5);
    const topBrands = Object.values(brandStats).sort((a, b) => b.cartons - a.cartons).slice(0, 5);
    const topGrades = Object.values(gradeStats).sort((a, b) => b.cartons - a.cartons).slice(0, 5);
    
    const maxProductCartons = topProducts.length > 0 ? topProducts[0].cartons : 1;
    const maxBrandCartons = topBrands.length > 0 ? topBrands[0].cartons : 1;
    const maxGradeCartons = topGrades.length > 0 ? topGrades[0].cartons : 1;
    
    return { topProducts, topBrands, topGrades, totalItems: allItems.length, maxProductCartons, maxBrandCartons, maxGradeCartons, productStats, brandStats, gradeStats };
  }, [allItems]);

  // Handle analytics item click
  const handleAnalyticsClick = (type, name) => {
    let data = [];
    let statsMap = {};
    
    if (type === 'product') {
      statsMap = analytics.productStats;
    } else if (type === 'brand') {
      statsMap = analytics.brandStats;
    } else if (type === 'grade') {
      statsMap = analytics.gradeStats;
    }
    
    if (statsMap[name]) {
      data = statsMap[name].items;
    }
    
    setAnalyticsModal({
      open: true,
      type,
      name,
      data
    });
  };

  const handleMonthToggle = (month) => {
    setExpandedMonths(prev => ({ ...prev, [month]: !prev[month] }));
  };

  const handleAccordionChange = (fclNo) => (event, isExpanded) => {
    setExpandedFcl(isExpanded ? fclNo : false);
    if (!isExpanded) {
      setSelectedProductInShipment(prev => ({ ...prev, [fclNo]: null }));
    }
  };

  const handleSelectAllInShipment = (items) => {
    const itemIds = items.map(item => item.id);
    const allSelected = itemIds.every(id => selectedItems.includes(id));
    if (allSelected) {
      setSelectedItems(selectedItems.filter(id => !itemIds.includes(id)));
    } else {
      setSelectedItems([...new Set([...selectedItems, ...itemIds])]);
    }
  };

  const handleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      setError('⚠️ Please select at least one item to delete');
      return;
    }
    if (!window.confirm(`⚠️ Are you sure you want to delete ${selectedItems.length} selected shipment item(s)? This action cannot be undone.`)) {
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/bulk_delete.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedItems })
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Successfully deleted ${data.deleted_count} record(s)`);
        setSelectedItems([]);
        fetchShipments();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(`❌ Failed to delete records: ${data.message}`);
      }
    } catch (err) {
      setError('❌ Error deleting records. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFclMonth('');
    setFclNo('');
    setPoNumber('');
    setStartDate('');
    setEndDate('');
    setProductFilter('');
    setGradeFilter('');
    setBrandFilter('');
    setSearchTerm('');
  };

  const isAnyFilterActive = () => {
    return fclMonth || fclNo || poNumber || startDate || endDate || productFilter || gradeFilter || brandFilter || searchTerm;
  };

  const getAllShipmentItems = () => {
    const items = [];
    shipments.forEach(shipment => {
      if (shipment.items) {
        shipment.items.forEach(item => {
          items.push({ ...item, fcl_month: shipment.fcl_month, fcl_no: shipment.fcl_no });
        });
      }
    });
    return items;
  };

  const getConsolidatedSummary = () => {
    const items = getAllShipmentItems();
    const summaryMap = {};
    items.forEach(item => {
      const key = `${item.product || 'N/A'}|${item.grade || 'N/A'}|${item.pack_size || 'N/A'}|${item.brand || 'N/A'}`;
      if (!summaryMap[key]) {
        summaryMap[key] = { product: item.product || 'N/A', grade: item.grade || 'N/A', pack_size: item.pack_size || 'N/A', brand: item.brand || 'N/A', entries: 0, cartons: 0, loose_bags: 0, total_kgs: 0, total_pounds: 0 };
      }
      summaryMap[key].entries += 1;
      summaryMap[key].cartons += Number(item.cartons) || 0;
      summaryMap[key].loose_bags += Number(item.loose_bags) || 0;
      summaryMap[key].total_kgs += Number(item.total_kgs) || 0;
      summaryMap[key].total_pounds += calculateTotalPounds(item.pack_size, item.cartons, item.loose_bags);
    });
    return Object.values(summaryMap).sort((a, b) => b.cartons - a.cartons);
  };

  const consolidatedSummary = getConsolidatedSummary();
  const allShipmentItems = getAllShipmentItems();

  const handleMarkExcess = (shipment) => { setSelectedShipment(shipment); setShowMarkExcessModal(true); };
  const handleReturnToInventory = (shipment) => { setSelectedShipment(shipment); setShowReturnToInventoryModal(true); };

  const handleProductClick = (fclNo, product, grade) => {
    setSelectedProductInShipment(prev => {
      const currentFilter = prev[fclNo];
      const newFilter = currentFilter?.product === product && currentFilter?.grade === grade ? null : { product, grade };
      return { ...prev, [fclNo]: newFilter };
    });
  };

  const getFilteredItems = (shipment) => {
    const filter = selectedProductInShipment[shipment.fcl_no];
    if (!filter) return shipment.items;
    return shipment.items.filter(item => item.product === filter.product && item.grade === filter.grade);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try { return format(new Date(dateString), 'MMM dd, yyyy'); } catch { return dateString; }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try { return format(new Date(dateString), 'MMM dd, yyyy hh:mm a'); } catch { return dateString; }
  };

  const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);

  const getRankEmoji = (rank) => {
    switch(rank) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return rank + 1;
    }
  };

  if (loading && shipments.length === 0) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="normal">📦 Shipments Dashboard</Typography>
        <Box display="flex" gap={2}>
          {user && user.role === 'admin' && (
            <>
              {selectedItems.length > 0 && (
                <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleBulkDelete}>
                  Delete Selected ({selectedItems.length})
                </Button>
              )}
              <Button variant="contained" color="success" startIcon={<CloudUploadIcon />} onClick={() => setShowImportModal(true)}>
                Import Data
              </Button>
            </>
          )}
          <ExportButton data={shipments} filename="shipments" label="Export Shipments" />
        </Box>
      </Box>

      {successMessage && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="normal">{summary.total_shipments}</Typography>
                  <Typography variant="body2">Total Shipments</Typography>
                </Box>
                <ShippingIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="normal">{formatNumber(summary.total_pallets)}</Typography>
                  <Typography variant="body2">Total Pallets</Typography>
                </Box>
                <Inventory2Icon sx={{ fontSize: 48, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="normal">{formatNumber(summary.total_cartons)}</Typography>
                  <Typography variant="body2">Total Cartons</Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="normal">{formatNumber(Number(summary.total_pounds || 0).toFixed(0))}</Typography>
                  <Typography variant="body2">Total Pounds</Typography>
                </Box>
                <ScaleIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Improved Analytics Section - Wider Cards, Clickable Items */}
      <Paper sx={{ p: 0, mb: 3, overflow: 'hidden', borderRadius: 2, boxShadow: 3 }}>
        <Box sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon sx={{ color: 'white', fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>Shipment Analytics</Typography>
            <Chip label={`${analytics.totalItems} items`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', ml: 1 }} />
          </Box>
          <Button size="small" onClick={() => setShowAnalytics(!showAnalytics)} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }} variant="outlined" endIcon={showAnalytics ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}>
            {showAnalytics ? 'HIDE' : 'SHOW'}
          </Button>
        </Box>
        
        <Collapse in={showAnalytics}>
          <Box sx={{ p: 3, bgcolor: '#f8f9fa' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, textAlign: 'center' }}>
              💡 Click on any item to see detailed breakdown
            </Typography>
            <Grid container spacing={3}>
              {/* Top Products - Wider Card */}
              <Grid item xs={12} md={4}>
                <Card elevation={2} sx={{ height: '100%', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', p: 2, borderRadius: '8px 8px 0 0' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CategoryIcon sx={{ color: 'white' }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="white">Top 5 Products</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>By cartons shipped</Typography>
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    {analytics.topProducts.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>No data available</Typography>
                    ) : (
                      analytics.topProducts.map((product, idx) => {
                        const percentage = (product.cartons / analytics.maxProductCartons) * 100;
                        return (
                          <Box 
                            key={idx} 
                            sx={{ 
                              mb: 2.5, 
                              cursor: 'pointer', 
                              p: 1, 
                              borderRadius: 1,
                              '&:hover': { bgcolor: '#e3f2fd' },
                              transition: 'background-color 0.2s'
                            }}
                            onClick={() => handleAnalyticsClick('product', product.name)}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                              <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{getRankEmoji(idx)}</Typography>
                                <Typography variant="body2" fontWeight={idx === 0 ? 'bold' : 'medium'}>
                                  {product.name}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0, ml: 1 }}>
                                <Typography variant="body2" fontWeight="bold" color="primary.main">{formatNumber(product.cartons)}</Typography>
                                <InfoIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                              </Box>
                            </Box>
                            <LinearProgress variant="determinate" value={percentage} sx={{ height: 10, borderRadius: 5, bgcolor: '#e3f2fd', '& .MuiLinearProgress-bar': { borderRadius: 5, background: idx === 0 ? 'linear-gradient(90deg, #d32f2f 0%, #1565c0 100%)' : '#90caf9' } }} />
                          </Box>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Top Brands - Wider Card */}
              <Grid item xs={12} md={4}>
                <Card elevation={2} sx={{ height: '100%', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #1565c0 0%, #1565c0 100%)', p: 2, borderRadius: '8px 8px 0 0' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <SellIcon sx={{ color: 'white' }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="white">Top 5 Brands</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>By cartons shipped</Typography>
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    {analytics.topBrands.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>No data available</Typography>
                    ) : (
                      analytics.topBrands.map((brand, idx) => {
                        const percentage = (brand.cartons / analytics.maxBrandCartons) * 100;
                        return (
                          <Box 
                            key={idx} 
                            sx={{ 
                              mb: 2.5, 
                              cursor: 'pointer', 
                              p: 1, 
                              borderRadius: 1,
                              '&:hover': { bgcolor: '#f3e5f5' },
                              transition: 'background-color 0.2s'
                            }}
                            onClick={() => handleAnalyticsClick('brand', brand.name)}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                              <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{getRankEmoji(idx)}</Typography>
                                <Typography variant="body2" fontWeight={idx === 0 ? 'bold' : 'medium'}>
                                  {brand.name}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0, ml: 1 }}>
                                <Typography variant="body2" fontWeight="bold" color="secondary.main">{formatNumber(brand.cartons)}</Typography>
                                <InfoIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                              </Box>
                            </Box>
                            <LinearProgress variant="determinate" value={percentage} sx={{ height: 10, borderRadius: 5, bgcolor: '#f3e5f5', '& .MuiLinearProgress-bar': { borderRadius: 5, background: idx === 0 ? 'linear-gradient(90deg, #9c27b0 0%, #ce93d8 100%)' : '#ce93d8' } }} />
                          </Box>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Top Grades - Wider Card */}
              <Grid item xs={12} md={4}>
                <Card elevation={2} sx={{ height: '100%', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)', p: 2, borderRadius: '8px 8px 0 0' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <GradeIcon sx={{ color: 'white' }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="white">Top 5 Grades</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>By cartons shipped</Typography>
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    {analytics.topGrades.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>No data available</Typography>
                    ) : (
                      analytics.topGrades.map((grade, idx) => {
                        const percentage = (grade.cartons / analytics.maxGradeCartons) * 100;
                        return (
                          <Box 
                            key={idx} 
                            sx={{ 
                              mb: 2.5, 
                              cursor: 'pointer', 
                              p: 1, 
                              borderRadius: 1,
                              '&:hover': { bgcolor: '#e8f5e9' },
                              transition: 'background-color 0.2s'
                            }}
                            onClick={() => handleAnalyticsClick('grade', grade.name)}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                              <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{getRankEmoji(idx)}</Typography>
                                <Typography variant="body2" fontWeight={idx === 0 ? 'bold' : 'medium'}>
                                  {grade.name}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0, ml: 1 }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ color: '#2e7d32' }}>{formatNumber(grade.cartons)}</Typography>
                                <InfoIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                              </Box>
                            </Box>
                            <LinearProgress variant="determinate" value={percentage} sx={{ height: 10, borderRadius: 5, bgcolor: '#e8f5e9', '& .MuiLinearProgress-bar': { borderRadius: 5, background: idx === 0 ? 'linear-gradient(90deg, #2e7d32 0%, #81c784 100%)' : '#a5d6a7' } }} />
                          </Box>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Filters - FIXED: Using fixed width to prevent truncation */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
          <FilterAltIcon color="primary" /> Filters
        </Typography>
        
        {/* Row 1: Main filters with fixed widths - NO TRUNCATION */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Autocomplete
            freeSolo
            size="small"
            options={fclMonths}
            value={fclMonth}
            onChange={(e, newValue) => setFclMonth(newValue || '')}
            onInputChange={(e, newValue) => setFclMonth(newValue || '')}
            sx={{ width: 140 }}
            renderInput={(params) => <TextField {...params} label="FCL Month" InputLabelProps={{ shrink: true }} />}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={fclNumbers}
            value={fclNo}
            onChange={(e, newValue) => setFclNo(newValue || '')}
            onInputChange={(e, newValue) => setFclNo(newValue || '')}
            sx={{ width: 100 }}
            renderInput={(params) => <TextField {...params} label="FCL No" InputLabelProps={{ shrink: true }} />}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={poNumbers}
            value={poNumber}
            onChange={(e, newValue) => setPoNumber(newValue || '')}
            onInputChange={(e, newValue) => setPoNumber(newValue || '')}
            sx={{ width: 150 }}
            renderInput={(params) => <TextField {...params} label="PO Number" InputLabelProps={{ shrink: true }} />}
          />
          <TextField 
            label="Start Date" 
            type="date" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)} 
            InputLabelProps={{ shrink: true }} 
            size="small"
            sx={{ width: 160 }}
          />
          <TextField 
            label="End Date" 
            type="date" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)} 
            InputLabelProps={{ shrink: true }} 
            size="small"
            sx={{ width: 160 }}
          />
          <Autocomplete 
            size="small" 
            options={PRODUCTS} 
            value={productFilter || null} 
            onChange={(e, newValue) => setProductFilter(newValue || '')} 
            sx={{ width: 140 }}
            renderInput={(params) => <TextField {...params} label="Product" InputLabelProps={{ shrink: true }} />} 
            freeSolo 
            autoHighlight 
            clearOnEscape 
          />
          <Autocomplete 
            size="small" 
            options={GRADES} 
            value={gradeFilter || null} 
            onChange={(e, newValue) => setGradeFilter(newValue || '')} 
            sx={{ width: 110 }}
            renderInput={(params) => <TextField {...params} label="Grade" InputLabelProps={{ shrink: true }} />} 
            freeSolo 
            autoHighlight 
            clearOnEscape 
          />
          <Autocomplete 
            size="small" 
            options={BRANDS} 
            value={brandFilter || null} 
            onChange={(e, newValue) => setBrandFilter(newValue || '')} 
            sx={{ width: 140 }}
            renderInput={(params) => <TextField {...params} label="Brand" InputLabelProps={{ shrink: true }} />} 
            freeSolo 
            autoHighlight 
            clearOnEscape 
          />
        </Box>

        {/* Row 2: Search and Clear */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField 
            label="Search" 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            placeholder="Pallet, Lot No..." 
            size="small" 
            sx={{ width: 280 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} 
          />
          <Button variant="outlined" onClick={handleClearFilters} size="medium" startIcon={<ClearIcon />}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Consolidated Summary */}
      {isAnyFilterActive() && consolidatedSummary.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, border: '2px solid #1565c0', backgroundColor: '#e3f2fd' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1 }}>
            📊 Consolidated Summary
            <Chip label={`${consolidatedSummary.length} groups`} size="small" color="primary" />
            <Chip label={`${allShipmentItems.length} items`} size="small" variant="outlined" />
          </Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: 'white' }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: 'white' }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: 'white' }}>Pack Size</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: 'white' }}>Brand</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: 'white' }}>Entries</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: 'white' }}>Cartons</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: 'white' }}>Loose Bags</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: 'white' }}>Total KGs</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: 'white' }}>Total LBs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consolidatedSummary.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#bbdefb' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.product}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.grade}</TableCell>
                    <TableCell>{row.pack_size}</TableCell>
                    <TableCell>{row.brand}</TableCell>
                    <TableCell align="right">{row.entries}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#1565c0' }}>{row.cartons.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.loose_bags.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#2e7d32' }}>{row.total_kgs.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#ed6c02' }}>{row.total_pounds.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: '#90caf9' }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 700 }}>GRAND TOTAL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{allShipmentItems.length}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#1565c0' }}>{formatNumber(summary.total_cartons)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>-</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32' }}>-</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#ed6c02' }}>{formatNumber(Number(summary.total_pounds || 0).toFixed(2))}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {isAnyFilterActive() && consolidatedSummary.length > 0 && (
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1 }}>
          📋 Shipment-wise Details
          <Chip label={`${shipments.length} shipments`} size="small" variant="outlined" color="primary" />
        </Typography>
      )}

      {/* Shipments List */}
      <Box>
        {shipments.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6" color="text.secondary">No shipments found</Typography></Paper>
        ) : (
          shipmentsGroupedByMonth.map((monthGroup) => (
            <Paper key={monthGroup.month} sx={{ mb: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, bgcolor: '#1565c0', color: 'white', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', '&:hover': { bgcolor: '#0d47a1' } }} onClick={() => handleMonthToggle(monthGroup.month)}>
                <Box display="flex" alignItems="center" gap={2}>
                  <CalendarIcon />
                  <Typography variant="h6" fontWeight="bold">{monthGroup.month}</Typography>
                  <Chip label={`${monthGroup.shipments.length} FCLs`} size="small" sx={{ bgcolor: 'white', color: '#1565c0', fontWeight: 'bold' }} />
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip label={`${formatNumber(monthGroup.totals.pallets)} Pallets`} size="small" sx={{ bgcolor: '#42a5f5', color: 'white' }} />
                  <Chip label={`${formatNumber(monthGroup.totals.cartons)} Cartons`} size="small" sx={{ bgcolor: '#66bb6a', color: 'white' }} />
                  <Chip label={`${formatNumber(monthGroup.totals.pounds.toFixed(0))} Lbs`} size="small" sx={{ bgcolor: '#ffa726', color: 'white' }} />
                  {expandedMonths[monthGroup.month] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </Box>
              </Box>

              <Collapse in={expandedMonths[monthGroup.month]}>
                <Box sx={{ p: 1 }}>
                  {monthGroup.shipments.map((shipment, index) => {
                    const filteredItems = getFilteredItems(shipment);
                    const activeFilter = selectedProductInShipment[shipment.fcl_no];
                    
                    return (
                      <Accordion key={shipment.fcl_no || index} expanded={expandedFcl === shipment.fcl_no} onChange={handleAccordionChange(shipment.fcl_no)} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
                            <Box display="flex" alignItems="center" gap={2}>
                              {user && user.role === 'admin' && (
                                <Checkbox checked={shipment.items.every(item => selectedItems.includes(item.id))} indeterminate={shipment.items.some(item => selectedItems.includes(item.id)) && !shipment.items.every(item => selectedItems.includes(item.id))} onChange={(e) => { e.stopPropagation(); handleSelectAllInShipment(shipment.items); }} onClick={(e) => e.stopPropagation()} />
                              )}
                              <Box>
                                <Typography variant="h6" fontWeight="bold">{shipment.fcl_no || 'NO FCL'}</Typography>
                                <Typography variant="body2" color="text.secondary">{formatDate(shipment.shipped_date)} • PO: {shipment.po_number || 'N/A'}</Typography>
                              </Box>
                            </Box>
                            <Box display="flex" gap={2}>
                              <Chip label={`${shipment.pallet_count || 0} Pallets`} color="info" size="small" icon={<Inventory2Icon />} />
                              <Chip label={`${shipment.product_count || 0} Products`} color="primary" size="small" />
                              <Chip label={`${formatNumber(shipment.totals.cartons)} Cartons`} color="success" size="small" />
                              <Chip label={`${formatNumber((shipment.items || []).reduce((sum, item) => sum + calculateTotalPounds(item.pack_size, item.cartons, item.loose_bags), 0).toFixed(0))} Lbs`} color="warning" size="small" />
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {shipment.product_summary && shipment.product_summary.length > 0 && (
                            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2" fontWeight="normal">📦 Products in this Shipment {activeFilter && '(Filtered)'}:</Typography>
                                {activeFilter && <Button size="small" startIcon={<ClearIcon />} onClick={() => handleProductClick(shipment.fcl_no, null, null)}>Clear Filter</Button>}
                              </Box>
                              <Grid container spacing={2}>
                                {shipment.product_summary.map((prod, idx) => {
                                  const isSelected = activeFilter?.product === prod.product && activeFilter?.grade === prod.grade;
                                  return (
                                    <Grid item xs={12} sm={6} md={4} key={idx}>
                                      <Paper sx={{ p: 1.5, bgcolor: isSelected ? '#e3f2fd' : 'white', border: '2px solid', borderColor: isSelected ? 'primary.main' : 'grey.300', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { borderColor: 'primary.main', boxShadow: 3 } }} onClick={() => handleProductClick(shipment.fcl_no, prod.product, prod.grade)}>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                          <Box flex={1}>
                                            <Typography variant="body2" fontWeight="normal" color="primary">{prod.product} - {prod.grade}</Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">{prod.pack_size} | {prod.brand}</Typography>
                                            <Box display="flex" gap={2} mt={1}>
                                              <Chip label={`${formatNumber(prod.cartons)} Cartons`} size="small" color="success" variant={isSelected ? "filled" : "outlined"} />
                                              <Chip label={`${formatNumber(Number(prod.pounds).toFixed(0))} Lbs`} size="small" color="warning" variant={isSelected ? "filled" : "outlined"} />
                                            </Box>
                                          </Box>
                                          {isSelected && <FilterAltIcon color="primary" />}
                                        </Box>
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Paper>
                          )}
                          
                          <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
                            <Button variant="contained" color="warning" size="small" startIcon={<WarningIcon />} onClick={() => handleMarkExcess(shipment)}>Mark Excess Cartons</Button>
                            <Button variant="contained" color="info" size="small" startIcon={<UndoIcon />} onClick={() => handleReturnToInventory(shipment)}>Return to Inventory</Button>
                          </Box>
                          
                          <TableContainer sx={{ maxHeight: "500px", overflowX: "auto", overflowY: "auto", "&::-webkit-scrollbar": { width: "10px", height: "10px" }, "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" }, "&::-webkit-scrollbar-thumb": { backgroundColor: "#888", borderRadius: "6px", "&:hover": { backgroundColor: "#555" } } }}>
                            <Table size="small" stickyHeader sx={{ minWidth: "1400px" }}>
                              <TableHead>
                                <TableRow>
                                  {user && user.role === 'admin' && (
                                    <TableCell padding="checkbox" sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 3 }}>
                                      <Checkbox checked={filteredItems.every(item => selectedItems.includes(item.id))} indeterminate={filteredItems.some(item => selectedItems.includes(item.id)) && !filteredItems.every(item => selectedItems.includes(item.id))} onChange={() => handleSelectAllInShipment(filteredItems)} sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }} />
                                    </TableCell>
                                  )}
                                  <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}>ID</TableCell>
                                  <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>Date of Shipment</TableCell>
                                  <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}>Product</TableCell>
                                  <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}>Grade</TableCell>
                                  <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}>Brand</TableCell>
                                  <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>Pack Size</TableCell>
                                  <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}>Cartons</TableCell>
                                  <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>Loose Bags</TableCell>
                                  <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>Loose KGs</TableCell>
                                  <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>Total Pounds</TableCell>
                                  <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>Pallet ID</TableCell>
                                  <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>Lot No</TableCell>
                                  <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}><Box display="flex" alignItems="center" gap={0.5}><AccessTimeIcon fontSize="small" />Entry Time</Box></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredItems.length === 0 ? (
                                  <TableRow><TableCell colSpan={user?.role === 'admin' ? 14 : 13} align="center"><Typography variant="body2" color="text.secondary" py={2}>No items found for the selected product filter</Typography></TableCell></TableRow>
                                ) : (
                                  filteredItems.map((item) => (
                                    <TableRow key={item.id} hover>
                                      {user && user.role === 'admin' && <TableCell padding="checkbox"><Checkbox checked={selectedItems.includes(item.id)} onChange={() => handleSelectItem(item.id)} /></TableCell>}
                                      <TableCell>{item.product_id}</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.shipped_date)}</TableCell>
                                      <TableCell>{item.product}</TableCell>
                                      <TableCell>{item.grade}</TableCell>
                                      <TableCell>{item.brand}</TableCell>
                                      <TableCell>{item.pack_size}</TableCell>
                                      <TableCell align="right">{formatNumber(item.cartons)}</TableCell>
                                      <TableCell align="right">{formatNumber(item.loose_bags)}</TableCell>
                                      <TableCell align="right">{Number(item.loose_kgs).toFixed(2)}</TableCell>
                                      <TableCell align="right">{formatNumber(calculateTotalPounds(item.pack_size, item.cartons, item.loose_bags).toFixed(2))}</TableCell>
                                      <TableCell>{item.pallet_id}</TableCell>
                                      <TableCell>{item.lot_no}</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: 'text.secondary' }}>{formatDateTime(item.updated_at)}</TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              </Collapse>
            </Paper>
          ))
        )}
      </Box>

      {/* Analytics Drill-Down Modal */}
      <Dialog 
        open={analyticsModal.open} 
        onClose={() => setAnalyticsModal({ open: false, type: '', name: '', data: [] })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: analyticsModal.type === 'product' ? '#1565c0' : analyticsModal.type === 'brand' ? '#6a1b9a' : '#2e7d32',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            {analyticsModal.type === 'product' && <CategoryIcon />}
            {analyticsModal.type === 'brand' && <SellIcon />}
            {analyticsModal.type === 'grade' && <GradeIcon />}
            <span>
              {analyticsModal.type.charAt(0).toUpperCase() + analyticsModal.type.slice(1)}: {analyticsModal.name}
            </span>
            <Chip label={`${analyticsModal.data.length} items`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
          </Box>
          <IconButton onClick={() => setAnalyticsModal({ open: false, type: '', name: '', data: [] })} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Summary Stats */}
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary">{formatNumber(analyticsModal.data.reduce((sum, item) => sum + (parseInt(item.cartons) || 0), 0))}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Cartons</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="success.main">{formatNumber(analyticsModal.data.reduce((sum, item) => sum + calculateTotalPounds(item.pack_size, item.cartons, item.loose_bags), 0).toFixed(0))}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Pounds</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="info.main">{new Set(analyticsModal.data.map(i => i.fcl_no)).size}</Typography>
                  <Typography variant="caption" color="text.secondary">FCLs</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">{new Set(analyticsModal.data.map(i => i.pallet_id)).size}</Typography>
                  <Typography variant="caption" color="text.secondary">Pallets</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
          
          {/* Detailed Table */}
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>FCL</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>Brand</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>Pack Size</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>Cartons</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>Pounds</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>Pallet ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}>Lot No</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyticsModal.data.map((item, idx) => (
                  <TableRow key={idx} hover sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                    <TableCell><Chip label={`${item.fcl_month}/${item.fcl_no}`} size="small" variant="outlined" /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.shipped_date)}</TableCell>
                    <TableCell>{item.product}</TableCell>
                    <TableCell>{item.grade}</TableCell>
                    <TableCell>{item.brand}</TableCell>
                    <TableCell>{item.pack_size}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(item.cartons)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#ed6c02' }}>{formatNumber(calculateTotalPounds(item.pack_size, item.cartons, item.loose_bags).toFixed(2))}</TableCell>
                    <TableCell>{item.pallet_id}</TableCell>
                    <TableCell>{item.lot_no}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAnalyticsModal({ open: false, type: '', name: '', data: [] })} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <MarkExcessModal open={showMarkExcessModal} onClose={() => { setShowMarkExcessModal(false); setSelectedShipment(null); }} shipment={selectedShipment} onSuccess={fetchShipments} />
      <ReturnToInventoryModal open={showReturnToInventoryModal} onClose={() => { setShowReturnToInventoryModal(false); setSelectedShipment(null); }} shipment={selectedShipment} onSuccess={fetchShipments} />
      <ImportDataModal open={showImportModal} onClose={() => setShowImportModal(false)} onSuccess={fetchShipments} importType="shipments" user={user} />
    </Container>
  );
};

export default ShipmentsPage;